class SubscriptionDiscountBadges {
  constructor() {
    this.debug = true; // Set to false in production
    this.init();
  }

  log(...args) {
    if (this.debug) {
      console.log('[SubscriptionBadges]', ...args);
    }
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bindEvents());
    } else {
      this.bindEvents();
    }
  }

  bindEvents() {
    this.log('Initializing subscription discount badges for Appstle...');
    
    // Detect Appstle subscription widget changes
    this.observeAppstle();
    
    // Listen for Appstle-specific radio button changes
    this.observeAppstleToggles();
    
    // Initial check with multiple delays to catch different loading scenarios
    setTimeout(() => {
      this.updateBadges();
      this.insertFreeShippingHTML();
      this.addShippingPriceText();
    }, 500);
    setTimeout(() => {
      this.updateBadges();
      this.insertFreeShippingHTML();
      this.addShippingPriceText();
    }, 1500);
    setTimeout(() => {
      this.updateBadges();
      this.insertFreeShippingHTML();
      this.addShippingPriceText();
    }, 3000);
    setTimeout(() => {
      this.insertFreeShippingHTML();
      this.addShippingPriceText();
    }, 5000); // Additional delay for late-loading elements
  }

  observeAppstle() {
    // Observer for Appstle widget DOM changes
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      let shouldCheckFreeShipping = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          // Check if Appstle-related elements changed
          if (this.hasAppstleElements(mutation.target)) {
            shouldUpdate = true;
            shouldCheckFreeShipping = true;
          }
          
          // Check added nodes for Appstle content
          if (mutation.addedNodes) {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1 && this.hasAppstleElements(node)) {
                shouldUpdate = true;
                shouldCheckFreeShipping = true;
              }
            });
          }
        }
      });

      if (shouldUpdate) {
        this.log('Appstle DOM mutation detected, updating badges');
        setTimeout(() => this.updateBadges(), 100);
      }
      
      if (shouldCheckFreeShipping) {
        this.log('Appstle DOM mutation detected, checking free shipping');
        setTimeout(() => this.insertFreeShippingHTML(), 200);
        setTimeout(() => this.insertFreeShippingHTML(), 1000); // Retry after longer delay
        setTimeout(() => this.addShippingPriceText(), 300);
        setTimeout(() => this.addShippingPriceText(), 1200); // Retry after longer delay
      }
    });

    // Start observing the document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'checked', 'selected', 'data-appstle-selected']
    });
  }

  observeAppstleToggles() {
    // Listen for clicks on Appstle radio buttons
    document.addEventListener('click', (e) => {
      const target = e.target;
      
      // Check for Appstle subscription radio buttons
      if (this.isAppstleRadio(target)) {
        this.log('Appstle radio clicked:', target);
        setTimeout(() => this.updateBadges(), 100);
        setTimeout(() => this.updateBadges(), 300);
      }
      
      // Check for Appstle labels
      if (target.matches('.appstle_radio_label') || 
          target.closest('.appstle_radio_label')) {
        this.log('Appstle label clicked:', target);
        setTimeout(() => this.updateBadges(), 100);
        setTimeout(() => this.updateBadges(), 300);
      }
    });

    // Listen for change events on Appstle radios
    document.addEventListener('change', (e) => {
      const target = e.target;
      
      if (this.isAppstleRadio(target)) {
        this.log('Appstle radio changed:', target);
        setTimeout(() => this.updateBadges(), 100);
      }
    });
  }

  isAppstleRadio(element) {
    return element && element.type === 'radio' && (
      element.name === 'selling_plan' ||
      element.id?.startsWith('appstle_selling_plan_label_') ||
      element.closest('.appstle_subscription_wrapper') ||
      element.closest('.appstle_sub_widget')
    );
  }

  hasAppstleElements(element) {
    return element && (
      element.className?.includes('appstle') ||
      element.id?.includes('appstle') ||
      element.querySelector('[class*="appstle"]') ||
      element.querySelector('[id*="appstle"]') ||
      element.closest('.appstle_sub_widget')
    );
  }

  isSubscriptionSelected() {
    this.log('Checking Appstle subscription selection...');
    
    // Method 1: Check for checked Appstle subscription radio
    const subscriptionRadio = document.querySelector('input[name="selling_plan"][id*="appstle_selling_plan_label_2"]:checked');
    if (subscriptionRadio && subscriptionRadio.value !== '') {
      this.log('Found subscription via Appstle subscription radio:', subscriptionRadio);
      return true;
    }
    
    // Method 2: Check for checked radio with subscription value
    const sellingPlanRadios = document.querySelectorAll('input[name="selling_plan"]:checked');
    for (const radio of sellingPlanRadios) {
      this.log('Checking selling plan radio:', radio.id, radio.value);
      
      // If value is not empty and it's the subscription option (usually has a selling plan ID)
      if (radio.value && radio.value !== '' && radio.id?.includes('appstle_selling_plan_label_2')) {
        this.log('Found subscription via selling plan radio with value:', radio.value);
        return true;
      }
    }
    
    // Method 3: Check for active subscription wrapper class
    const subscriptionWrapper = document.querySelector('.appstle_subscription_wrapper_option:not(.appstle-active-option)');
    const oneTimeWrapper = document.querySelector('.appstle_subscription_wrapper_option.appstle-active-option');
    
    if (subscriptionWrapper && !oneTimeWrapper) {
      this.log('Found subscription via wrapper classes - subscription active');
      return true;
    }
    
    if (oneTimeWrapper) {
      // Check if the one-time wrapper contains subscription elements
      const oneTimeRadio = oneTimeWrapper.querySelector('input[name="selling_plan"]');
      if (oneTimeRadio && oneTimeRadio.value === '') {
        this.log('Found one-time purchase via active wrapper');
        return false;
      }
    }
    
    // Method 4: Check subscription text in labels
    const checkedRadios = document.querySelectorAll('input[name="selling_plan"]:checked');
    for (const radio of checkedRadios) {
      const label = document.querySelector(`label[for="${radio.id}"]`);
      const text = (label?.textContent || '').toLowerCase();
      
      this.log('Radio label text:', text);
      
      if (text.includes('subscribe') || text.includes('subscription')) {
        this.log('Found subscription via label text:', text);
        return true;
      }
      
      if (text.includes('one time') || text.includes('onetime')) {
        this.log('Found one-time via label text:', text);
        return false;
      }
    }
    
    // Method 5: Check for selected subscription elements by class
    const selectedSubscription = document.querySelector('.appstle_subscription_wrapper .appstle_subscription_radio_wrapper input:checked');
    if (selectedSubscription) {
      this.log('Found subscription via selected subscription wrapper');
      return true;
    }
    
    // Method 6: Check URL params for selling plan
    const urlParams = new URLSearchParams(window.location.search);
    const sellingPlan = urlParams.get('selling_plan');
    if (sellingPlan && sellingPlan !== '') {
      this.log('Found subscription via URL selling_plan:', sellingPlan);
      return true;
    }
    
    this.log('No subscription detected, defaulting to one-time');
    return false;
  }

  updateBadges() {
    const isSubscription = this.isSubscriptionSelected();
    const badges = document.querySelectorAll('.variant-discount-badge');
    
    this.log('Updating badges:', { isSubscription, badgeCount: badges.length });
    
    // Toggle js-onetime class on parent elements
    this.toggleOneTimeClass(!isSubscription);
    
    if (badges.length === 0) {
      this.log('No badges found - variant picker may not be loaded yet');
      return;
    }
    
    badges.forEach((badge, index) => {
      const subscriptionText = badge.getAttribute('data-subscription-text') || '';
      const onetimeText = badge.getAttribute('data-onetime-text') || '';
      
      this.log(`Badge ${index}:`, { subscriptionText, onetimeText, isSubscription });
      
      let newText = '';
      
      if (isSubscription && subscriptionText) {
        newText = subscriptionText;
        badge.classList.add('subscription-active');
        badge.classList.remove('onetime-active');
      } else if (!isSubscription && onetimeText) {
        newText = onetimeText;
        badge.classList.add('onetime-active');
        badge.classList.remove('subscription-active');
      } else {
        // Hide badge if no appropriate text
        badge.style.display = 'none';
        return;
      }
      
      badge.style.display = 'flex';
      
      // Only update text if it's different to avoid unnecessary DOM changes
      if (badge.textContent.trim() !== newText.trim()) {
        badge.textContent = newText;
        this.log(`Updated badge ${index} text to:`, newText);
      }
    });
  }

  toggleOneTimeClass(isOneTime) {
    this.log('Toggling js-onetime class:', { isOneTime });
    
    // Find potential parent elements to apply the class to
    const targetElements = [
      document.querySelector('.product-form'),
      document.querySelector('.product-form__variants'),
      document.querySelector('.product-form__input--pills'),
      document.querySelector('#appstle_subscription_widget0'),
      document.querySelector('.appstle_subscription_wrapper'),
      document.querySelector('main'),
      document.querySelector('.product'),
      document.querySelector('.product__info-wrapper'),
      document.body
    ].filter(Boolean); // Remove null elements
    
    // Also target all individual pill input containers
    const pillElements = document.querySelectorAll('.product-form__input--pills, .product-form__input, .variant-pill-wrapper, .N-variant-picker-new .product-form__input');
    
    // Apply to main target elements
    targetElements.forEach(element => {
      if (isOneTime) {
        element.classList.add('js-onetime');
        this.log('Added js-onetime class to:', element.className || element.tagName);
      } else {
        element.classList.remove('js-onetime');
        this.log('Removed js-onetime class from:', element.className || element.tagName);
      }
    });
    
    // Apply to all pill elements
    pillElements.forEach(element => {
      if (isOneTime) {
        element.classList.add('js-onetime');
        this.log('Added js-onetime class to pill:', element.className || element.tagName);
      } else {
        element.classList.remove('js-onetime');
        this.log('Removed js-onetime class from pill:', element.className || element.tagName);
      }
    });
    
    // Apply onetime-green class to variant pill buttons only
    const pillButtons = document.querySelectorAll('.product-form__input--pills input[type="radio"] + label, .variant-pill-wrapper input[type="radio"] + label');
    
    pillButtons.forEach(button => {
      if (isOneTime) {
        button.classList.add('onetime-green');
        this.log('Added onetime-green class to pill button:', button.className);
      } else {
        button.classList.remove('onetime-green');
        this.log('Removed onetime-green class from pill button:', button.className);
      }
    });
  }

  // IMPROVED FUNCTION: Insert "OR _ free shipping" HTML above one-time wrapper
  insertFreeShippingHTML() {
    this.log('Attempting to insert free shipping HTML...');
    
    // Try multiple selectors to find the one-time wrapper option
    let oneTimeWrapperOption = null;
    
    // Method 1: Look for one-time text wrapper
    oneTimeWrapperOption = document.querySelector('.appstle_subscription_wrapper_option:has(.appstle_one_time_text)');
    
    // Method 2: Look for wrapper with one-time radio button (empty value)
    if (!oneTimeWrapperOption) {
      const wrappers = document.querySelectorAll('.appstle_subscription_wrapper_option');
      for (const wrapper of wrappers) {
        const radio = wrapper.querySelector('input[name="selling_plan"]');
        if (radio && radio.value === '') {
          oneTimeWrapperOption = wrapper;
          break;
        }
      }
    }
    
    // Method 3: Look for wrapper containing "one time" text
    if (!oneTimeWrapperOption) {
      const wrappers = document.querySelectorAll('.appstle_subscription_wrapper_option');
      for (const wrapper of wrappers) {
        const text = wrapper.textContent.toLowerCase();
        if (text.includes('one time') || text.includes('onetime')) {
          oneTimeWrapperOption = wrapper;
          break;
        }
      }
    }
    
    // Method 4: Look for any subscription wrapper option (fallback)
    if (!oneTimeWrapperOption) {
      oneTimeWrapperOption = document.querySelector('.appstle_subscription_wrapper_option');
    }
    
    if (!oneTimeWrapperOption) {
      this.log('One-time wrapper option not found yet');
      return;
    }
    
    this.log('Found one-time wrapper option:', oneTimeWrapperOption);
    
    // Check if we already added the HTML (prevent duplicates)
    const existingFreeShipping = document.querySelector('.custom-free-shipping-text');
    if (existingFreeShipping) {
      this.log('Free shipping HTML already exists');
      return;
    }
    
    // Create the HTML element with new design
    const freeShippingHTML = this.createFreeShippingElement();
    
    // Insert it RIGHT BEFORE the one-time wrapper option (as a sibling)
    oneTimeWrapperOption.parentNode.insertBefore(freeShippingHTML, oneTimeWrapperOption);
    
    this.log('Free shipping HTML inserted successfully above one-time wrapper');
  }

  // Create the free shipping HTML element with updated design
  createFreeShippingElement() {
    const freeShippingDiv = document.createElement('div');
    freeShippingDiv.className = 'custom-free-shipping-text';
    freeShippingDiv.innerHTML = `
      <div class="free-shipping-banner">
        <div class="free-shipping-content">
          <span class="free-shipping-icon"><img style="width: 25px;height: 25px; object-fit: contain;" src="https://cdn.shopify.com/s/files/1/0797/9011/3102/files/shipping.png?v=1749576950"></span>
          <span class="free-shipping-text">Free Shipping</span>
        </div>
        <div class="free-shipping-divider">
          <span class="or-text">OR</span>
        </div>
      </div>
    `;
    
    // Add inline styles to match the screenshot design
    freeShippingDiv.style.cssText = `
      margin: 6px 0;
      width: 100%;
    `;
    
    const banner = freeShippingDiv.querySelector('.free-shipping-banner');
    banner.style.cssText = `
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
    `;
    
    const content = freeShippingDiv.querySelector('.free-shipping-content');
    content.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #495057;
      margin-bottom: 10px;
      width: fit-content;
    `;

    
    
    const icon = freeShippingDiv.querySelector('.free-shipping-icon');
    icon.style.cssText = `
      font-size: 16px;
    `;
    
    const text = freeShippingDiv.querySelector('.free-shipping-text');
    text.style.cssText = `
      font-size: 14px;
      color: #000;
    `;
    
    const divider = freeShippingDiv.querySelector('.free-shipping-divider');
    divider.style.cssText = `
      position: relative;
      width: 100%;
      height: 1px;
      background-color: #000;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    const orText = freeShippingDiv.querySelector('.or-text');
    orText.style.cssText = `
      background-color: white;
      padding: 0 15px;
      font-size: 12px;
      font-weight: 500;
      color: #000;
      text-transform: uppercase;
      letter-spacing: 1px;
    `;
    
    return freeShippingDiv;
  }

  // Function to modify one-time text and add shipping price
  // addShippingPriceText() {
  //   this.log('Attempting to enhance one-time text with shipping price...');
    
  //   // Find the one-time text element
  //   const oneTimeTextElement = document.querySelector('.appstle_one_time_text');
    
  //   if (!oneTimeTextElement) {
  //     this.log('One-time text element not found yet');
  //     return;
  //   }
    
  //   // Check if we already modified this element
  //   if (oneTimeTextElement.closest('.one-time-custom-wrapper')) {
  //     this.log('One-time text already modified');
  //     return;
  //   }
    
  //   this.log('Found one-time text element:', oneTimeTextElement);
    
  //   // Get the shipping price from metafield
  //   const shippingPrice = this.getShippingPriceMetafield();
    
  //   if (!shippingPrice) {
  //     this.log('No shipping price metafield found');
  //     return;
  //   }
    
  //   // Get the current text content
  //   const currentText = oneTimeTextElement.textContent.trim();
    
  //   // Create wrapper div
  //   const wrapperDiv = document.createElement('div');
  //   wrapperDiv.className = 'one-time-custom-wrapper';
  //   wrapperDiv.style.cssText = `
  //     display: flex;
  //     flex-direction: column;
  //     gap: 2px;
  //   `;
    
  //   // Create original text div
  //   const originalTextDiv = document.createElement('div');
  //   originalTextDiv.className = 'one-time-original-text';
  //   originalTextDiv.textContent = currentText;
    
  //   // Create shipping price div
  //   const shippingPriceDiv = document.createElement('div');
  //   shippingPriceDiv.className = 'one-time-shipping-price';
  //   shippingPriceDiv.textContent = shippingPrice;
  //   shippingPriceDiv.style.cssText = `
  //     font-size: 12px;
  //     color: #666;
  //     font-weight: normal;
  //   `;
    
  //   // Replace the original text element with our wrapper
  //   oneTimeTextElement.parentNode.insertBefore(wrapperDiv, oneTimeTextElement);
  //   wrapperDiv.appendChild(originalTextDiv);
  //   wrapperDiv.appendChild(shippingPriceDiv);
  //   oneTimeTextElement.remove();
    
  //   this.log('One-time text modified successfully with shipping price:', shippingPrice);
  // }

  // Get shipping price from metafield
  // getShippingPriceMetafield() {
  //   this.log('Looking for shipping price metafield...');
    
  //   // Method 1: Try to get from window.productData.product_metafields (based on the structure seen)
  //   if (window.productData && window.productData.product_metafields && window.productData.product_metafields.product_shipping_price) {
  //     this.log('Found shipping price in window.productData.product_metafields:', window.productData.product_metafields.product_shipping_price);
  //     return window.productData.product_metafields.product_shipping_price;
  //   }
    
  //   // Method 2: Try to get from window.meta.product.metafields
  //   if (window.meta && window.meta.product && window.meta.product.metafields) {
  //     const metafields = window.meta.product.metafields;
  //     if (metafields.custom && metafields.custom.product_shipping_price) {
  //       this.log('Found shipping price in window.meta:', metafields.custom.product_shipping_price);
  //       return metafields.custom.product_shipping_price;
  //     }
  //   }
    
  //   // Method 3: Try to get from window.product.metafields
  //   if (window.product && window.product.metafields) {
  //     const metafields = window.product.metafields;
  //     if (metafields.custom && metafields.custom.product_shipping_price) {
  //       this.log('Found shipping price in window.product:', metafields.custom.product_shipping_price);
  //       return metafields.custom.product_shipping_price;
  //     }
  //   }
    
  //   // Method 4: Try to find in DOM data attributes
  //   const productContainer = document.querySelector('[data-product-metafields]');
  //   if (productContainer) {
  //     try {
  //       const metafields = JSON.parse(productContainer.getAttribute('data-product-metafields'));
  //       if (metafields.custom && metafields.custom.product_shipping_price) {
  //         this.log('Found shipping price in DOM data:', metafields.custom.product_shipping_price);
  //         return metafields.custom.product_shipping_price;
  //       }
  //     } catch (e) {
  //       this.log('Error parsing metafields from DOM:', e);
  //     }
  //   }
    
  //   // Method 5: Try to find script tag with product data
  //   const productScripts = document.querySelectorAll('script[type="application/json"]');
  //   for (const script of productScripts) {
  //     try {
  //       const data = JSON.parse(script.textContent);
  //       if (data.metafields && data.metafields.custom && data.metafields.custom.product_shipping_price) {
  //         this.log('Found shipping price in script tag:', data.metafields.custom.product_shipping_price);
  //         return data.metafields.custom.product_shipping_price;
  //       }
  //     } catch (e) {
  //       // Continue to next script
  //     }
  //   }
    
  //   // Method 6: Try to get from Shopify global variables
  //   if (typeof Shopify !== 'undefined' && Shopify.product && Shopify.product.metafields) {
  //     const metafields = Shopify.product.metafields;
  //     if (metafields.custom && metafields.custom.product_shipping_price) {
  //       this.log('Found shipping price in Shopify global:', metafields.custom.product_shipping_price);
  //       return metafields.custom.product_shipping_price;
  //     }
  //   }
    
  //   // Default fallback - you can customize this
  //   this.log('No shipping price metafield found, using fallback');
  //   return '+ Shipping cost';
  // }
}



// Initialize when the script loads
new SubscriptionDiscountBadges();